// The Sweep page: pick a template, edit the size/rate matrix, run it strictly
// one cell at a time, watch a progress matrix fill in, and explore the results
// via heatmaps / loss-vs-size / rtt-vs-rate plus a per-cell drill-down.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store';
import type { CellResult, SweepResult, SweepSpec } from '@/engine/types';
import { enumerateCells } from '@/engine/sweep';
import { Heatmap, LossVsSize, RttVsPps, severityColor, useChartTheme } from '@/charts';
import { SWEEP_TEMPLATES, sweepTemplateById } from '@/lib/presets';
import { formatIntList, parseIntList, pct } from '@/lib/format';
import { exportPayloadJSON } from '@/lib/export';
import { makeSweepPayload } from '@/lib/share';
import {
  CopyButton,
  EmptyState,
  Field,
  NodePicker,
  NumberInput,
  Panel,
  ProgressBar,
  Segmented,
  TextInput,
  TierBadge,
} from './components';
import { RunReport } from './results';

interface Draft {
  sizesText: string;
  ppsText: string;
  pairsText: string;
  durationMs: number;
  acceptableDelayMs: number;
  warmupMs: number;
  repeats: number;
  order: SweepSpec['order'];
}

function templateDraft(templateId: string, warmupMs: number): Draft {
  const tpl = sweepTemplateById(templateId);
  if (!tpl) {
    return {
      sizesText: '212',
      ppsText: '15',
      pairsText: '',
      durationMs: 8000,
      acceptableDelayMs: 200,
      warmupMs,
      repeats: 1,
      order: 'size-major',
    };
  }
  return {
    sizesText: formatIntList(tpl.sizes ?? []),
    ppsText: formatIntList(tpl.pps ?? []),
    pairsText: (tpl.pairs ?? []).map(([s, p]) => `${s}/${p}`).join(', '),
    durationMs: tpl.durationMs,
    acceptableDelayMs: tpl.acceptableDelayMs,
    warmupMs,
    repeats: tpl.repeats,
    order: 'size-major',
  };
}

function parsePairs(text: string): [number, number][] {
  const out: [number, number][] = [];
  for (const token of text.split(/[,\n]+/)) {
    const m = token.trim().match(/^(\d+)\s*[/x:]\s*(\d+)$/);
    if (m) out.push([Number(m[1]), Number(m[2])]);
  }
  return out;
}

function draftToSpec(draft: Draft, nodeIds: string[]): SweepSpec {
  const pairs = parsePairs(draft.pairsText);
  return {
    nodes: nodeIds,
    sizes: parseIntList(draft.sizesText),
    pps: parseIntList(draft.ppsText),
    pairs: pairs.length > 0 ? pairs : undefined,
    durationMs: draft.durationMs,
    acceptableDelayMs: draft.acceptableDelayMs,
    warmupMs: draft.warmupMs,
    repeats: Math.max(1, draft.repeats),
    order: draft.order,
  };
}

/** The fill-in progress matrix. */
function ProgressMatrix({
  spec,
  cells,
  runningIndex,
  onPick,
}: {
  spec: SweepSpec;
  cells: CellResult[];
  runningIndex: number | null;
  onPick: (index: number) => void;
}) {
  const { palette } = useChartTheme();
  const planned = useMemo(() => enumerateCells(spec), [spec]);
  const done = cells.length;
  return (
    <div className="matrix">
      {planned.map((c) => {
        const isDone = c.index < done;
        const isRunning = c.index === runningIndex;
        const cell = isDone ? cells[c.index] : undefined;
        const bg = cell ? severityColor(palette, cell.run.metrics.totalLossPct) : undefined;
        return (
          <button
            key={c.index}
            type="button"
            className={`matrix__cell${isRunning ? ' is-running' : ''}${isDone ? ' is-done' : ''}`}
            style={bg ? { background: bg, color: '#111' } : undefined}
            disabled={!isDone}
            onClick={() => onPick(c.index)}
            title={`${c.size} B · ${c.pps} pps${cell ? ` · ${cell.run.metrics.totalLossPct.toFixed(1)}%` : ''}`}
          >
            <span className="matrix__size">{c.size}</span>
            <span className="matrix__pps">{c.pps}pps</span>
            {cell ? <span className="matrix__loss">{pct(cell.run.metrics.totalLossPct, 0)}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function SweepPage() {
  const { t } = useTranslation();
  const nodes = useStore((s) => s.nodes);
  const settings = useStore((s) => s.settings);
  const sweepPhase = useStore((s) => s.sweepPhase);
  const sweepSpec = useStore((s) => s.sweepSpec);
  const sweepNodes = useStore((s) => s.sweepNodes);
  const sweepCells = useStore((s) => s.sweepCells);
  const sweepProgress = useStore((s) => s.sweepProgress);
  const sweepResult = useStore((s) => s.sweepResult);
  const engineError = useStore((s) => s.engineError);
  const startSweep = useStore((s) => s.startSweep);
  const stopSweep = useStore((s) => s.stopSweep);
  const persistSweep = useStore((s) => s.persistSweep);
  const sweepSavedId = useStore((s) => s.sweepSavedId);

  const [templateId, setTemplateId] = useState<string>(SWEEP_TEMPLATES[0]?.id ?? 'custom');
  const [draft, setDraft] = useState<Draft>(() =>
    templateDraft(SWEEP_TEMPLATES[0]?.id ?? 'custom', settings.warmupMs),
  );
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [drill, setDrill] = useState<number | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);

  const isBusy = sweepPhase === 'connecting' || sweepPhase === 'running';

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = sweepTemplateById(id);
    if (tpl) setDraft(templateDraft(id, settings.warmupMs));
  };

  const patch = (p: Partial<Draft>) => {
    setTemplateId('custom');
    setDraft((d) => ({ ...d, ...p }));
  };

  const previewSpec = useMemo(() => draftToSpec(draft, selectedNodes), [draft, selectedNodes]);
  const cellCount = useMemo(
    () => (selectedNodes.length > 0 ? enumerateCells(previewSpec).length : 0),
    [previewSpec, selectedNodes],
  );

  // A live-updating sweep result to feed the charts even mid-run.
  const liveSweep: SweepResult | null = useMemo(() => {
    if (sweepResult) return sweepResult;
    if (sweepSpec && sweepCells.length > 0) return { spec: sweepSpec, cells: sweepCells };
    return null;
  }, [sweepResult, sweepSpec, sweepCells]);

  const drillCell = useMemo(() => {
    if (drill === null) return null;
    return sweepCells[drill] ?? sweepResult?.cells[drill] ?? null;
  }, [drill, sweepCells, sweepResult]);

  const nodeById = useMemo(() => new Map(sweepNodes.map((n) => [n.id, n])), [sweepNodes]);

  useEffect(() => {
    setDrill(null);
    setShareLink(null);
  }, [sweepPhase]);

  const start = () => {
    if (selectedNodes.length === 0) return;
    void startSweep(draftToSpec(draft, selectedNodes));
  };

  const doShare = async () => {
    const id = await persistSweep(true);
    if (id) setShareLink(`${window.location.origin}/#/share?id=${id}`);
  };

  const runningIndex = sweepProgress ? sweepProgress.index : null;
  const total = sweepProgress?.total ?? cellCount;
  const doneCount = sweepCells.length;

  return (
    <div className="page sweep">
      <p className="lead">{t('sweep.intro')}</p>

      <div className="sweep__grid">
        <div className="sweep__config">
          <Panel
            title={t('sweep.title')}
            actions={
              <select
                className="input"
                value={templateId}
                disabled={isBusy}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                {SWEEP_TEMPLATES.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
                <option value="custom">{t('sweep.templateCustom')}</option>
              </select>
            }
          >
            <Field label={t('sweep.sizes')} htmlFor="sw-sizes">
              <TextInput
                id="sw-sizes"
                value={draft.sizesText}
                disabled={isBusy}
                onChange={(v) => patch({ sizesText: v })}
              />
            </Field>
            <Field label={t('sweep.ppsList')} htmlFor="sw-pps">
              <TextInput
                id="sw-pps"
                value={draft.ppsText}
                disabled={isBusy}
                onChange={(v) => patch({ ppsText: v })}
              />
            </Field>
            <Field label={t('sweep.pairs')} hint={t('sweep.pairsHint')} htmlFor="sw-pairs">
              <TextInput
                id="sw-pairs"
                value={draft.pairsText}
                placeholder="1100/60, 1150/120"
                disabled={isBusy}
                onChange={(v) => patch({ pairsText: v })}
              />
            </Field>
            <div className="grid2">
              <Field label={t('settings.durationMs')} htmlFor="sw-dur">
                <NumberInput
                  id="sw-dur"
                  value={draft.durationMs}
                  min={1000}
                  step={1000}
                  disabled={isBusy}
                  onChange={(n) => patch({ durationMs: n })}
                />
              </Field>
              <Field label={t('settings.acceptableDelay')} htmlFor="sw-delay">
                <NumberInput
                  id="sw-delay"
                  value={draft.acceptableDelayMs}
                  min={1}
                  disabled={isBusy}
                  onChange={(n) => patch({ acceptableDelayMs: n })}
                />
              </Field>
            </div>
            <div className="grid2">
              <Field label={t('sweep.repeats')} htmlFor="sw-rep">
                <NumberInput
                  id="sw-rep"
                  value={draft.repeats}
                  min={1}
                  max={20}
                  disabled={isBusy}
                  onChange={(n) => patch({ repeats: n })}
                />
              </Field>
              <Field label={t('sweep.order')} htmlFor="sw-order">
                <Segmented
                  value={draft.order}
                  onChange={(v) => patch({ order: v })}
                  options={[
                    { value: 'size-major', label: t('sweep.orderSizeMajor') },
                    { value: 'pps-major', label: t('sweep.orderPpsMajor') },
                  ]}
                />
              </Field>
            </div>
          </Panel>

          <Panel title={t('sweep.node')}>
            <NodePicker
              nodes={nodes}
              selected={selectedNodes}
              onToggle={(id) =>
                setSelectedNodes((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                )
              }
              disabled={isBusy}
            />
            <p className="muted">{t('sweep.cells', { count: cellCount })}</p>
          </Panel>

          <div className="startbar">
            {isBusy ? (
              <button type="button" className="btn btn--danger btn--lg" onClick={stopSweep}>
                {t('sweep.stop')}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary btn--lg"
                onClick={start}
                disabled={selectedNodes.length === 0 || cellCount === 0}
              >
                {t('sweep.start')}
              </button>
            )}
            <p className="sequential-note">{t('run.sequentialNote')}</p>
          </div>
        </div>

        <div className="sweep__results">
          {engineError ? <p className="error">{engineError}</p> : null}

          {sweepSpec && (isBusy || sweepResult) && (
            <Panel
              title={t('sweep.progressMatrix')}
              actions={
                total > 0 ? (
                  <span className="muted">
                    {t('sweep.progress', { index: Math.min(doneCount, total), total })}
                  </span>
                ) : null
              }
            >
              <ProgressBar value={total > 0 ? doneCount / total : 0} />
              <ProgressMatrix
                spec={sweepSpec}
                cells={sweepCells}
                runningIndex={runningIndex}
                onPick={setDrill}
              />
            </Panel>
          )}

          {liveSweep && liveSweep.cells.length > 0 && (
            <>
              {sweepResult && (
                <div className="results__actions">
                  <button type="button" className="btn btn--primary" onClick={doShare}>
                    {t('common.share')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void persistSweep(false)}
                    disabled={sweepSavedId != null}
                  >
                    {sweepSavedId != null ? t('history.saved') : t('history.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => exportPayloadJSON(makeSweepPayload(sweepResult, sweepNodes))}
                  >
                    {t('common.exportJson')}
                  </button>
                  {shareLink ? <CopyButton text={shareLink} label={t('common.copyLink')} /> : null}
                </div>
              )}

              <Panel title={t('sweep.heatmaps')}>
                <Heatmap sweep={liveSweep} axis="size" nodes={sweepNodes} />
              </Panel>
              <div className="chartgrid chartgrid--2">
                <Panel title={t('sweep.lossVsSize')}>
                  <LossVsSize sweep={liveSweep} nodes={sweepNodes} />
                </Panel>
                <Panel title={t('sweep.rttVsPps')}>
                  <RttVsPps sweep={liveSweep} nodes={sweepNodes} />
                </Panel>
              </div>
            </>
          )}

          {drillCell ? (
            <Panel
              title={
                <span className="runhead">
                  <TierBadge tier={drillCell.run.tier} />
                  <span>
                    {t('sweep.drill')}: {drillCell.size} B · {drillCell.pps} pps
                  </span>
                </span>
              }
              actions={
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDrill(null)}>
                  {t('common.close')}
                </button>
              }
            >
              <RunReport run={drillCell.run} node={nodeById.get(drillCell.nodeId)} />
            </Panel>
          ) : liveSweep && liveSweep.cells.length > 0 ? (
            <p className="hint">{t('sweep.drillHint')}</p>
          ) : null}

          {!liveSweep && !isBusy && !engineError && <EmptyState>{t('sweep.noNode')}</EmptyState>}
        </div>
      </div>
    </div>
  );
}
